import Select from '@components/elements/select-2/select-2';
import {
  v2_GateDeliverySuggestion,
  v2_useCreateNewCart,
  v2_useProductList,
  v2_getProductTitles,
} from '@crocodille/common';
import { assertSome, isSome, Maybe, noop } from '@crocodille/util';
import cn from 'classnames';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { analyticsEvent } from 'src/modules/analytics';
import { isInWebView } from 'src/modules/webview';
import { AddressSearchResult } from '../../address-search/address-search';
import Error from '../../elements/error/error';
import styles from './shipping-gate-delivery-form.module.scss';
import Button from '@components/elements/button/button';
import { useAlert } from '@components/alert/alert';
import { getAddToCartError } from '@crocodille/bbdomu-i18n';

export interface ShippingGateDeliveryFormProps {
  suggestions: readonly v2_GateDeliverySuggestion[];
  navigateToProductList?(): void;
}

const ShippingGateDeliveryForm: React.FC<
  React.PropsWithChildren<ShippingGateDeliveryFormProps>
> = ({ navigateToProductList = noop, suggestions: suggestionOptions }) => {
  const { t } = useTranslation();
  const [suggestion, setSuggestion] =
    useState<Maybe<AddressSearchResult>>(null);

  const dispatchAlert = useAlert();
  const { data: products } = v2_useProductList();

  const {
    trigger: createNewCart,
    pendingCart,
    error: newCartError,
  } = v2_useCreateNewCart(
    (cart) => {
      analyticsEvent('add_shipping_info', {
        ecommerce: {
          shipping_tier: `${cart.shippingMethodName} - ${cart.branchCode}`,
        },
      });

      navigateToProductList();
    },
    {
      onError: (e) => {
        if (e && typeof e === 'object') {
          if ('kind' in e && typeof e.kind === 'string') {
            dispatchAlert(
              t(getAddToCartError(e.kind), {
                products:
                  e.kind === 'product_unavailable' && products
                    ? ` (${v2_getProductTitles(
                        products,
                        (e as unknown as { productVariantCodes: string[] })
                          .productVariantCodes,
                      ).join(', ')})`
                    : null,
              }),
              t(getAddToCartError('other')),
            );
          }
        }
      },
    },
  );

  const handleSubmitEvent = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!suggestion || suggestion.role !== 'gate' || !suggestion.gate) {
        return;
      }

      await createNewCart({
        branchCode: suggestion.code,
        delivery: {
          deliveryMethod: 'gate',
          gate: suggestion.gate,
        },
      });
    },
    [createNewCart, suggestion],
  );

  const selectValue = suggestion?.role === 'gate' ? suggestion.key : '';

  const isBusy = Boolean(pendingCart);
  const isValid = isSome(suggestion);

  return (
    <>
      <form className={styles.container} onSubmit={handleSubmitEvent}>
        <div className={styles.fields}>
          <Select
            value={selectValue}
            className={styles.select}
            onChange={(item) => {
              setSuggestion({
                role: 'gate',
                ...assertSome(suggestionOptions.find((s) => s.key === item)),
              });
            }}
          >
            {suggestionOptions.map((item) => (
              <Select.Item key={item.key} value={item.key} label={item.name} />
            ))}
          </Select>
          <Button
            type="submit"
            round
            size="large"
            disabled={isSome(suggestion) && (!isValid || isBusy)}
            className={cn(styles.submitButton, {
              [styles.isInWebView]: isInWebView,
            })}
          >
            <span className={styles.visibleDesktop}>
              {t('shipping.begin_order')}
            </span>
            <span className={styles.visibleMobile}>
              {t('shipping.begin_order_short')}
            </span>
          </Button>
        </div>
        {isSome(newCartError) ? (
          <Error>{newCartError.toString?.()}</Error>
        ) : null}
      </form>
    </>
  );
};

export default ShippingGateDeliveryForm;
